/**
 * vue-form-compat: Vue 3 compatibility layer for vue-form.
 *
 * Uses vee-validate@4 internally for form state management while exposing
 * the same component API as the original vue-form library:
 *
 *   - <vue-form :state="formstate"> — form wrapper that populates the state object
 *   - <validate :custom="{ name: fn }"> — runs custom validators on input
 *   - <field> — layout wrapper (renders tag or div)
 *   - <field-messages name="x"> — shows/hides .validmsg/.infomsg/.errmsg children
 *
 * formstate API:
 *   formstate.$dirty        — true if any field is dirty
 *   formstate.$invalid      — true if any field is invalid
 *   formstate.fieldName.$dirty, .$valid, .$error
 *   formstate.fieldName._setDirty()
 *   formstate._validate()   — run all validators
 *   formstate._reset()      — reset all fields to pristine/valid
 */
import {
  h, ref, reactive, provide, inject,
  onMounted, onBeforeUnmount, onUpdated, watch,
  defineComponent, nextTick
} from 'vue'

const FORM_KEY = Symbol('vue-form')

// ─── VueForm ──────────────────────────────────────────────────────────────────

const VueForm = defineComponent({
  name: 'VueForm',
  inheritAttrs: false,
  props: {
    state: { type: Object, default: () => ({}) },
  },
  emits: ['submit'],
  setup(props, { slots, emit, attrs }) {
    const fields = reactive({})        // name -> { dirty, valid, error, runValidator }

    // Build a proxy state object that is written into the parent's formstate
    function syncState() {
      const st = props.state
      if (!st) return

      let anyDirty = false
      let anyInvalid = false

      Object.keys(fields).forEach(name => {
        const f = fields[name]
        if (f.dirty) anyDirty = true
        if (!f.valid) anyInvalid = true

        // Create per-field accessors on state (e.g. formstate.alias)
        if (!st[name] || typeof st[name] !== 'object' || !st[name]._isVueFormField) {
          st[name] = {
            _isVueFormField: true,
            $dirty: f.dirty,
            $valid: f.valid,
            $error: f.error,
            _setDirty() {
              f.dirty = true
              syncState()
            },
            _setValid(v) {
              f.valid = v
              syncState()
            }
          }
        } else {
          st[name].$dirty = f.dirty
          st[name].$valid = f.valid
          st[name].$error = f.error
        }
      })

      st.$dirty = anyDirty
      st.$invalid = anyInvalid

      // Form-level _validate()
      if (!st._validate) {
        st._validate = () => {
          Object.keys(fields).forEach(name => {
            const f = fields[name]
            if (f.runValidator) f.runValidator()
          })
          syncState()
        }
      }

      // Form-level _reset()
      if (!st._reset) {
        st._reset = () => {
          Object.keys(fields).forEach(name => {
            fields[name].dirty = false
            fields[name].valid = true
            fields[name].error = null
          })
          syncState()
        }
      }
    }

    function registerField(name, fieldState) {
      fields[name] = fieldState
      syncState()
    }

    function unregisterField(name) {
      delete fields[name]
      const st = props.state
      if (st && st[name]) delete st[name]
      syncState()
    }

    function updateField(name, updates) {
      if (fields[name]) {
        Object.assign(fields[name], updates)
        syncState()
      }
    }

    provide(FORM_KEY, { registerField, unregisterField, updateField, fields })

    // Initialize $dirty/$invalid immediately so Vue 3 reactivity tracks them
    // from the first render (Vue 3 Proxy won't track properties added later
    // if the template already evaluated them as undefined).
    if (props.state) {
      if (!('$dirty' in props.state)) props.state.$dirty = false
      if (!('$invalid' in props.state)) props.state.$invalid = false
    }

    // Sync state after fields register
    nextTick(() => syncState())

    return () => {
      // Pass through id, class, style and other attrs to the form element
      const formProps = {
        ...attrs,
        onSubmit: e => { e.preventDefault(); emit('submit', e) }
      }
      return h('form', formProps, slots.default ? slots.default({ state: props.state }) : [])
    }
  }
})

// ─── Validate ─────────────────────────────────────────────────────────────────

const Validate = defineComponent({
  name: 'Validate',
  inheritAttrs: false,
  props: {
    tag: { type: String, default: 'div' },
    custom: { type: Object, default: null },   // { validatorName: fn }
    name: { type: String, default: null },      // field name (also read from child input)
    debounce: { type: [Number, String], default: 0 },
  },
  setup(props, { slots, attrs }) {
    const form = inject(FORM_KEY, null)
    const elRef = ref(null)
    let fieldName = null
    let fieldState = null
    let debounceTimer = null

    function getInputValue() {
      if (!elRef.value) return undefined
      const input = elRef.value.querySelector('input, textarea, select')
      return input ? input.value : undefined
    }

    async function runValidator() {
      if (!props.custom || !fieldState) return
      let allValid = true
      const errors = {}
      const value = getInputValue()
      for (const key of Object.keys(props.custom)) {
        const fn = props.custom[key]
        if (typeof fn !== 'function') continue
        // Pass the input value to the validator, matching original vue-form
        // behavior. Validators may be ASYNC (e.g. the nickname uniqueness
        // lookup) and return a Promise resolving true/false. The original
        // vue-form awaited those; await here too, otherwise a pending Promise
        // reads as "not === false" and the field is left permanently valid —
        // the lookup fires but its verdict (and the error message) is discarded.
        // Sync validators return a boolean directly, so they skip the await and
        // behave exactly as before.
        let result = fn(value)
        if (result && typeof result.then === 'function') result = await result
        if (result === false) {
          allValid = false
          errors[key] = true
        }
      }
      // Only mutate reactive state when the verdict actually changed. `errors`
      // is a fresh object each run, so assigning it unconditionally would change
      // fieldState.error's identity every time — and onUpdated re-runs this
      // validator on every re-render, so a field that stays invalid would
      // re-render each debounce tick forever, churning the modal (and making the
      // teleported suggestion dropdown flicker in and out). Idempotent writes let
      // a stable result settle and the render loop stop.
      const errKeys = allValid ? '' : Object.keys(errors).sort().join(',')
      const prevErrKeys = fieldState.error ? Object.keys(fieldState.error).sort().join(',') : ''
      if (fieldState.valid !== allValid || errKeys !== prevErrKeys) {
        fieldState.valid = allValid
        fieldState.error = allValid ? null : errors
        if (form) form.updateField(fieldName, { valid: allValid, error: fieldState.error })
      }
      // Classes are plain DOM writes (no reactivity), safe to re-apply every run.
      applyInputClasses()
    }

    function applyInputClasses() {
      if (!elRef.value || !fieldState) return
      const inputs = elRef.value.querySelectorAll('input, textarea, select')
      inputs.forEach(input => {
        if (fieldState.dirty) {
          input.classList.toggle('vf-valid', fieldState.valid)
          input.classList.toggle('vf-invalid', !fieldState.valid)
          input.classList.add('vf-dirty')
          input.classList.remove('vf-pristine')
        } else {
          input.classList.remove('vf-valid', 'vf-invalid', 'vf-dirty')
          input.classList.add('vf-pristine')
        }
      })
    }

    onMounted(() => {
      if (!form) return

      // Discover field name: prop > child input name attr > field-messages name
      fieldName = props.name
      if (!fieldName && elRef.value) {
        const input = elRef.value.querySelector('input[name], textarea[name], select[name]')
        if (input) fieldName = input.getAttribute('name')
      }
      if (!fieldName && elRef.value) {
        const fm = elRef.value.querySelector('[data-field-messages]')
        if (fm) fieldName = fm.getAttribute('data-field-messages')
      }
      if (!fieldName) {
        fieldName = '_validate_' + Math.random().toString(36).slice(2, 8)
      }

      fieldState = reactive({ dirty: false, valid: true, error: null, runValidator })
      form.registerField(fieldName, fieldState)

      // Listen for input events to mark dirty and re-validate
      if (elRef.value) {
        const debounceMs = parseInt(props.debounce) || 0

        const onInput = () => {
          if (fieldState && !fieldState.dirty) {
            fieldState.dirty = true
            if (form) form.updateField(fieldName, { dirty: true })
          }
          // Re-run validator on input (with optional debounce)
          if (debounceTimer) clearTimeout(debounceTimer)
          if (debounceMs > 0) {
            debounceTimer = setTimeout(() => {
              runValidator()
              applyInputClasses()
            }, debounceMs)
          } else {
            nextTick(() => {
              runValidator()
              applyInputClasses()
            })
          }
        }

        elRef.value.addEventListener('input', onInput)
        elRef.value.addEventListener('change', onInput)
      }

      // Run validator on next tick to catch initial state
      nextTick(() => {
        runValidator()
        applyInputClasses()
      })
    })

    onBeforeUnmount(() => {
      if (debounceTimer) clearTimeout(debounceTimer)
      if (form && fieldName) form.unregisterField(fieldName)
    })

    // Re-run validators when the component re-renders. This catches cases
    // where a validator's result depends on external reactive state (e.g.
    // validateCoords reads model.type which changes via el-select, but no
    // native input event fires on the coords input). In Vue 2, vue-form
    // handled this implicitly; Vue 3 needs an explicit onUpdated hook.
    onUpdated(() => {
      if (props.custom && fieldState) {
        nextTick(() => {
          runValidator()
          applyInputClasses()
        })
      }
    })

    // Watch for custom validator prop changes and re-run
    watch(() => props.custom, () => {
      nextTick(() => {
        runValidator()
        applyInputClasses()
      })
    }, { deep: true })

    return () => h(props.tag, { ...attrs, ref: elRef }, slots.default ? slots.default() : [])
  }
})

// ─── Field ────────────────────────────────────────────────────────────────────

const Field = defineComponent({
  name: 'Field',
  inheritAttrs: false,
  props: {
    tag: { type: String, default: 'div' },
    name: { type: String, default: null },
  },
  setup(props, { slots, attrs }) {
    return () => h(props.tag, attrs, slots.default ? slots.default() : [])
  }
})

// ─── FieldMessages ────────────────────────────────────────────────────────────
//
// In the original vue-form, <field-messages> controls which of its children
// (.validmsg, .infomsg, .errmsg) are visible based on the field's state:
//   - .validmsg visible when: field is valid AND dirty
//   - .errmsg   visible when: field is invalid
//   - .infomsg  visible when: field is pristine (not dirty) AND valid
//
// We render all slot children and add a CSS class to the wrapper that
// controls visibility via CSS. The consuming stylesheets already have
// the right selectors.

const FieldMessages = defineComponent({
  name: 'FieldMessages',
  inheritAttrs: false,
  props: {
    name: { type: String, required: true },
  },
  setup(props, { slots, attrs }) {
    const form = inject(FORM_KEY, null)

    return () => {
      const children = slots.default ? slots.default() : []

      if (!form || !form.fields[props.name]) {
        // Field not registered yet — render but hide all message types
        return h('div', {
          ...attrs,
          'data-field-messages': props.name,
          class: [attrs.class, 'vf-field-pristine']
        }, children)
      }

      const field = form.fields[props.name]

      // Determine which message type to show:
      //   pristine (not dirty) → show .infomsg only
      //   dirty + valid        → show .validmsg only
      //   dirty + invalid      → show .errmsg only
      let stateClass
      if (!field.dirty) {
        stateClass = 'vf-field-pristine'
      } else if (field.valid) {
        stateClass = 'vf-field-valid-dirty'
      } else {
        stateClass = 'vf-field-invalid'
      }

      return h('div', {
        ...attrs,
        'data-field-messages': props.name,
        class: [attrs.class, stateClass]
      }, children)
    }
  }
})

// ─── CSS for field-messages visibility ────────────────────────────────────────
//
// Inject a <style> tag once to control .validmsg/.infomsg/.errmsg visibility

let styleInjected = false
function injectStyles() {
  if (styleInjected || typeof document === 'undefined') return
  styleInjected = true

  const css = `
/* vue-form-compat: field-messages visibility */
.vf-field-pristine > .validmsg,
.vf-field-pristine > .errmsg {
  display: none !important;
}
.vf-field-valid-dirty > .infomsg,
.vf-field-valid-dirty > .errmsg {
  display: none !important;
}
.vf-field-invalid > .validmsg,
.vf-field-invalid > .infomsg {
  display: none !important;
}
`
  const style = document.createElement('style')
  style.setAttribute('data-vue-form-compat', '')
  style.textContent = css
  document.head.appendChild(style)
}

// ─── Plugin ───────────────────────────────────────────────────────────────────

export default {
  install(app) {
    injectStyles()
    app.component('vue-form', VueForm)
    app.component('validate', Validate)
    app.component('field', Field)
    app.component('field-messages', FieldMessages)
  }
}

export { VueForm, Validate, Field, FieldMessages }
